import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Table, Spinner, Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FetchPackingById } from "slices/Warehouse/Packing/IssuePacking/thunk";
import { MecordColors } from "Constants/Color";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import LoadingSpinner from "components/LoadingSpinner";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import CancelButton from "Common/Components/Buttons/CancelButton";

const WarehousePackingViewDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const { issuePacking, loading, issuePackingError } = useSelector(
    (state: any) => state.IssuePackingReducer,
  );

  // Extract the actual data. The JSON shows "data" at the root.
  // Assuming issuePacking from Redux holds the "data" object or the full response.
  // If it holds the full response, we access .data.
  const packingData = issuePacking?.data || issuePacking || {};

  useEffect(() => {
    if (id) {
      dispatch(FetchPackingById(id));
    }
  }, [dispatch, id]);

  const handleViewModal = (item: any) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  // Helper to format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      })
      .replace(/ /g, "-");
  };

  const salesOrderDate = packingData.sales_order_date;
  const etaDate = packingData.valid_until_date;
  const customer = packingData.customer_details || {};
  const primaryContact = customer.contact_details?.[0] || {};
  const billTo = packingData.bill_to || {};
  const billFrom = packingData.bill_from || {};
  const products =packingData.sales_order_product_items || packingData.items ||  [];

  return (
    <PageContainer>
      <Card className="shadow-none border-0">
        <Card.Header>
          <PageBreadcrumb
            title="View Details"
            titleLink="/warehouse-packing"
            breadcrumbs={[
              { label: "Issue Packing", to: "/warehouse-packing" },
              { label: "View Details", to: "" },
            ]}
            backNavigationPath={-1}
          />
          <hr />
        </Card.Header>

        {loading ? (
          <LoadingSpinner />
        ) : issuePackingError ? (
          <div className="text-center text-danger py-5">
            <h5>{issuePackingError}</h5>
          </div>
        ) : (
          <Card.Body>
            {/* Header */}

            {/* Sales Order Details */}
            <h5 className="fw-bold mb-3">Sales Order Details</h5>
            <div className="mb-4">
              <Row>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      SO No. :
                    </Col>
                    <Col sm={8}>{packingData.sales_order_number || "-"}</Col>{" "}
                    {/* Fallback or constructed SO No */}
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      SO Date :
                    </Col>
                    <Col sm={8}>{formatDate(salesOrderDate)}</Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Initiated By :
                    </Col>
                    <Col sm={8}>{packingData.initiated_by || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Valid Until Date:
                    </Col>
                    <Col sm={8}>{formatDate(etaDate)}</Col>
                  </Row>
                </Col>
              </Row>
              <Col md={6}>
             <Row className="mt-2">
              <Col sm={4} className="fw-bold">
                Tags:
              </Col>
              <Col sm={4}>
                {(packingData?.tags ?? []).length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {(packingData?.tags ?? []).map((tag: string) => (
                      <span
                        key={tag}
                        className="badge bg-light text-dark border"
                        style={{ padding: "4px 8px", fontSize: "11px" }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span>-</span>
                )}
              </Col>
            </Row>
              
              </Col>
              
            </div>

            {/* Company details */}
            <h5 className="fw-bold mb-3">Company details</h5>
            <div
              className="p-4 rounded-3 mb-4"
              style={{ backgroundColor: MecordColors.ActiveBackground }}
            >
              <Row>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Company Name :
                    </Col>
                    <Col sm={8}>{customer.company_name || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Company Email :
                    </Col>
                    <Col sm={8}>{customer.email || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Contact Name :
                    </Col>
                    <Col sm={8}>{primaryContact.name || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Phone Number :
                    </Col>
                    <Col sm={8}>{primaryContact.phone_number || "-"}</Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Industry :
                    </Col>
                    <Col sm={8}>{customer.industry || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Company Phone No. :
                    </Col>
                    <Col sm={8}>{customer.phone_number || "-"}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm={4} className="fw-bold">
                      Email :
                    </Col>
                    <Col sm={8}>{primaryContact.email || "-"}</Col>
                  </Row>
                </Col>
              </Row>
            </div>

            {/* Billing & Shipping Details */}
            <h5 className="fw-bold mb-3">Billing & Shipping Details</h5>
            <div
              className="p-4 rounded-3 mb-4"
              style={{ backgroundColor: MecordColors.ActiveBackground }}
            >
              <Row>
                <Col md={6}>
                  <Row>
                    <Col sm={3} className="fw-bold">
                      Bill From :
                    </Col>
                    <Col sm={9}>
                      <div>{billFrom.org_address}</div>
                      <div>
                        {billFrom.org_city}, {billFrom.org_state},{" "}
                        {billFrom.org_country} - {billFrom.org_pincode}
                      </div>
                      {/* <div>Phone: +91-{billFrom.phone_number || "9876543210"}</div> */}
                    </Col>
                  </Row>
                </Col>
                <Col md={6}>
                  <Row>
                    <Col sm={3} className="fw-bold">
                      Bill To :
                    </Col>
                    <Col sm={9}>
                      <div>{billTo.address}</div>
                      <div>
                        {billTo.city}, {billTo.state}, {billTo.country} -{" "}
                        {billTo.pincode}
                      </div>
                      <div>Phone: +91-{billTo.phone_number}</div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            {/* Product Details */}
            <h5 className="fw-bold mb-3">Product Details</h5>
            <div
              className="mb-4"
              style={{ backgroundColor: MecordColors.ActiveBackground }}
            >
              <Table
                responsive
                className="align-middle text-center mb-0"
                style={{ borderSpacing: "0 8px" }}
              >
                <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                  <tr>
                    <th className="py-3">S.No</th>
                    <th className="py-3 text-start">Products</th>
                    <th className="py-3">Product Code</th>
                    <th className="py-3">Category</th>
                    <th className="py-3">Sub-Category</th>
                    <th className="py-3">Make</th>
                    <th className="py-3">Model</th>
                    <th className="py-3">Order Quantity</th>
                    <th className="py-3">Packed Quantity</th>
                    <th className="py-3">Balance Quantity</th>
                    <th
                      className="py-3"
                      style={{
                        minWidth: "130px",
                        position: "sticky",
                        right: 0,
                        background: MecordColors.LightGrayColor,
                        zIndex: 2,
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? (
                    products.map((item: any, index: number) => {
                      // Using data directly from the API response items
                      const packedQty = item.packed_quantity || 0;
                      const balanceQty = item.balance_quantity || 0;

                      return (
                        <tr
                          key={item.sales_order_product_item_id || index}
                          style={{
                            backgroundColor: "#fff",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <td>{index + 1}</td>
                          <td className="text-start">
                            <div className="fw-bold">{item.product_name}</div>
                            <div className="text-muted small">
                              SKU: {item.product_sku || "-"} &nbsp; HSN Code:{" "}
                              {item.hsn_code || "-"}
                            </div>
                          </td>
                          <td>{item.product_code || item.product_sku}</td>
                          <td>{item.category_name}</td>
                          <td>{item.sub_category_name}</td>
                          <td>
                            <div>{item.make_name}</div>
                          </td>
                          <td>
                            <div>{item.model_name}</div>
                          </td>
                          <td>{item.product_quantity}</td>
                          <td>{packedQty}</td>
                          <td>{balanceQty}</td>
                          <td
                            className="py-3"
                            style={{
                              minWidth: "130px",
                              position: "sticky",
                              right: 0,
                              background: "white",
                              zIndex: 2,
                            }}
                          >
                            <button
                              className="btn btn-sm"
                              style={{
                                backgroundColor: MecordColors?.ThemeBGColor,
                                color: "#FFF",
                              }}
                              onClick={() => handleViewModal(item)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center py-4">
                        No Products Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Notes and Attachments */}
            {/* Notes and Attachments */}
            <div className="mb-4">
              <Table bordered className="mb-0 align-middle">
                <tbody>
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td className="fw-bold" style={{ width: "200px" }}>
                      Notes
                    </td>
                    <td style={{ whiteSpace: "pre-wrap" }}>{packingData.notes || "-"}</td>
                  </tr>
                  <tr style={{ backgroundColor: "#fff" }}>
                    <td className="fw-bold">Attachment</td>
                    <td>
                      {packingData.attachments &&
                      packingData.attachments.length > 0 ? (
                        packingData.attachments.map(
                          (att: string, i: number) => {
                            // Extract filename for display
                            const parts = att.split("/");
                            const filename = parts[parts.length - 1];
                            return (
                              <div key={i}>
                                {" "}
                                <a
                                  href={att}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {filename}
                                </a>
                              </div>
                            );
                          },
                        )
                      ) : (
                        <span className="text-muted">No File Uploaded</span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
              <CancelButton
                onClick={() => navigate(-1)}
                showConfirmation={false}
              />
            </div>

            {/* Serial Number View Modal */}
            <Modal
              show={showModal}
              onHide={() => setShowModal(false)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title className="fw-bold">Packing Details</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedItem && (
                  <>
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3">Product Details</h6>
                      <Table
                        bordered
                        responsive
                        className="text-center align-middle"
                      >
                        <thead
                          style={{
                            backgroundColor: MecordColors.LightGrayColor,
                          }}
                        >
                          <tr>
                            <th>Products</th>
                            <th>Product Code</th>
                            <th>Make</th>
                            <th>Model</th>
                            <th>Order Qty</th>
                            <th>Packed Qty</th>
                            <th>Balance Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="text-start">
                              <div>{selectedItem.product_name}</div>
                              <div className="small text-muted d-flex gap-2">
                                {" "}
                                <span className="fw-bold">SKU:</span>{" "}
                                {selectedItem.product_sku}
                                &nbsp;{" "}
                                <span className="fw-bold">HSN Code:</span>{" "}
                                {selectedItem.hsn_code}
                              </div>
                            </td>
                            <td>
                              {selectedItem.product_code ||
                                selectedItem.product_sku}
                            </td>
                            <td>{selectedItem.make_name}</td>
                            <td>{selectedItem.model_name}</td>
                            <td>{selectedItem.product_quantity}</td>
                            <td>{selectedItem.packed_quantity || 0}</td>
                            <td>{selectedItem.balance_quantity || 0}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    <div>
                      <h6 className="fw-bold mb-3">Serial Numbers</h6>
                      {selectedItem.serial_numbers &&
                      selectedItem.serial_numbers.length > 0 ? (
                        <Table
                          bordered
                          hover
                          responsive
                          className="text-center align-middle mb-0"
                        >
                          <thead
                            style={{
                              backgroundColor: MecordColors.LightGrayColor,
                            }}
                          >
                            <tr>
                              <th style={{ width: "100px" }}>Sr. No.</th>
                              <th>Serial Number</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedItem.serial_numbers.map(
                              (sn: string, idx: number) => (
                                <tr
                                  key={idx}
                                  style={{ backgroundColor: "#fff" }}
                                >
                                  <td>{idx + 1}</td>
                                  <td>{sn}</td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="p-3 border rounded bg-light text-center text-muted">
                          No serial numbers packed for this item.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <CancelButton
                  onClick={() => setShowModal(false)}
                  showConfirmation={false}
                />
              </Modal.Footer>
            </Modal>
          </Card.Body>
        )}
      </Card>
    </PageContainer>
  );
};

export default WarehousePackingViewDetails;
